import {
  Box,
  Button,
  Field,
  Input,
  InputGroup,
  Image,
  Select,
  Stack,
  Flex,
  VStack,
  createListCollection,
  FormatNumber,
  Float,
  Textarea,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import {
  useGroupMembers,
  type EstimateGasResponse,
  type LuckyboxSupportChainResponse,
  type SessionStore,
} from '@halo/chat-sdk-react';
import {
  CopyIcon,
  DefAvatarIcon,
  onImageError,
  toaster,
  tools,
  UserItem,
} from '@halo/ui-chat-react';
import type { CoinState, DerivedWallet, DerivedWalletId } from '@halo/wallet-app';
import { BIF, checkNumber } from '@halo/wallet-app';
import { useDeferredValue } from 'react';
import { safeJsonParse } from '@halo/utils';
import { useActionsStore } from '@/store/actions';
import { useWalletStore } from '@/wallet';
import WarningIcon from '@/assets/images/common/svg/warning.svg?react';
import type { CoverBgType } from './helpers';
import { coverOptions } from './helpers';
import { LuckyType } from '.';

export interface LuckyFormValues {
  quantity: string;
  myAccount: DerivedWalletId;
  sendTo?: string;
  amount: string;
  totalAmount: string;
  message?: string;
  coverBgType?: string;
  box_type: LuckyType;
  chainType: string;
  chat_id: string;
  box_icon: string;
  account: DerivedWallet;
  coin: CoinState;
  mainCoin: CoinState;
  gasFeeInfo: EstimateGasResponse;
}

interface LuckyFormProps {
  type: LuckyType;
  isGroup?: boolean;
  sessionStore?: SessionStore;
  onConfirm: (data: LuckyFormValues) => void;
}

export function LuckyForm({ type, isGroup, sessionStore, onConfirm }: LuckyFormProps) {
  const { t } = useTranslation();
  const { fetchProfileUser } = useActionsStore();
  const { client, session, user, groupInfo } = sessionStore || {};
  const { accounts, curFiat, getAccountNetCoins, getCurrencyPrice } = useWalletStore();

  const { members, run } = useGroupMembers(session?.chat_id);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    trigger,
    control,
  } = useForm<LuckyFormValues>();
  const [sendTo, setSendTo] = useState<string>();
  const [amount, setAmount] = useState<string>();
  const [quantity, setQuantity] = useState<string>();

  const [chains, setChains] = useState<LuckyboxSupportChainResponse[]>([]);
  const [myAccount, setMyAccount] = useState<DerivedWalletId>();
  const [coins, setCoins] = useState<LuckyFormValues['coin'][]>([]);
  const [coin, setCoin] = useState<any>();
  const [gasFeeUSD, setGasFeeUSD] = useState('0');
  const [loading, setLoading] = useState(false);
  const [gasFeeInfo, setGasFeeInfo] = useState<EstimateGasResponse>({
    expected_amount: 0,
    expected_amount_str: '0',
    reserve_lamports_str: '0',
  } as EstimateGasResponse);

  const [coverBgType, setCoverBgType] = useState<CoverBgType>();

  const message = watch('message');

  const totalAmount = useMemo(() => {
    if (!checkNumber(amount) || !checkNumber(quantity)) return '0';
    return type === LuckyType.Identical ? new BIF(amount).mul(quantity).toString() : amount;
  }, [amount, quantity, type]);

  const isMinAmount = useMemo(() => {
    if (checkNumber(amount) && checkNumber(quantity) && type === LuckyType.Random) {
      return new BIF(amount).div(quantity).lt(0.01);
    }
    return false;
  }, [amount, quantity, type]);

  const currentNet = useMemo(() => {
    const chain = chains[0];
    return {
      id: `${chain?.chain_type}_${chain?.list?.[0]}`,
      chainType: chain?.chain_type,
    };
  }, [chains]);

  const curCoin = useMemo(() => {
    return coins?.find((item) => item?.id === coin);
  }, [coin, coins]);

  const curMainCoin = useMemo(() => {
    return coins?.find((item) => item?.isMain);
  }, [coins]);

  const value = useDeferredValue(
    JSON.stringify({ curCoin, curMainCoin, totalAmount, quantity, isMinAmount })
  );

  const accountsCollection = useMemo(() => {
    return createListCollection({
      items: accounts?.flatMap(({ derivedWallets, ...restAccount }) => {
        return derivedWallets.map((item) => ({
          ...restAccount,
          pid: restAccount.id,
          pName: restAccount.name,
          label: item.name,
          value: item.id,
          ...item,
        }));
      }),
    });
  }, [accounts]);

  const membersCollection = useMemo(() => {
    return createListCollection({
      items:
        members?.map((member) => ({
          label: member.name,
          value: member.user_id,
          ...member,
        })) || [],
    });
  }, [members]);

  const coinsCollection = useMemo(() => {
    return createListCollection({
      items:
        coins?.map((coin) => ({
          label: coin.symbol,
          value: coin.id,
          ...coin,
        })) || [],
    });
  }, [coins]);

  const isAmountGtBalance = useMemo(() => {
    if (!Number(totalAmount) || !Number(quantity)) return false;
    return (
      Number(gasFeeInfo.expected_amount_str) > Number(curCoin?.balance || 0) ||
      Number(gasFeeInfo.expected_amount_str) === 0
    );
  }, [curCoin?.balance, gasFeeInfo.expected_amount_str, quantity, totalAmount]);

  const onMemberAvatarClick = useCallback(
    async (member: any) => {
      fetchProfileUser(member.user_id);
    },
    [fetchProfileUser]
  );

  const onSubmit = handleSubmit(async (data) => {
    if (isAmountGtBalance) {
      toaster.error({
        title: t('tips.amountSentExceedsBalanceText'),
      });
      return;
    }
    setLoading(true);
    try {
      await onConfirm({
        ...data,
        box_type: type,
        chat_id: session!.chat_id,
        box_icon: coverBgType!,
        account: accountsCollection.items.find((item) => item.value === data.myAccount)!,
        coin: curCoin!,
        mainCoin: curMainCoin!,
        totalAmount: totalAmount,
        gasFeeInfo,
        // sendToInfo: membersCollection.items.find((item) => item.value === sendTo),
      });
    } finally {
      setLoading(false);
    }
  });

  const getGasFeeInfo = useCallback(async () => {
    const { curCoin, curMainCoin, totalAmount, quantity, isMinAmount } = safeJsonParse(
      value,
      {} as any
    );
    if (!client?.Api || !curCoin || !curMainCoin || !totalAmount || !quantity || isMinAmount) {
      return;
    }
    const { data } = await client.Api.luckyBox.getLuckyBoxEstimateGas({
      is_main_token: !!curCoin?.isMain,
      main_cmc_id: curMainCoin?.cmc_id,
      cmc_id: curCoin?.isMain ? undefined : curCoin?.cmc_id,
      chain_type: curCoin?.chainType,
      chain_id: curCoin?.chainId?.toString(),
      total_amount: totalAmount,
      box_count: Number(quantity),
    });
    if (data) {
      setGasFeeInfo(data);
    } else {
      setGasFeeInfo({
        expected_amount: 0,
        expected_amount_str: '0',
        reserve_lamports_str: '0',
      } as EstimateGasResponse);
    }
  }, [client?.Api, value]);

  useLayoutEffect(() => {
    if (type === LuckyType.Random) {
      setSendTo(membersCollection.items[0]?.value);
    }

    setMyAccount(myAccount || accountsCollection.items[0]?.value);
    setCoverBgType(coverBgType || coverOptions.items[0]?.value);
    setCoin(coin || coinsCollection.items[0]?.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsCollection.items, membersCollection.items, coinsCollection.items, type]);

  useEffect(() => {
    getGasFeeInfo();
  }, [getGasFeeInfo]);

  useEffect(() => {
    if (myAccount && currentNet?.id) {
      (async () => {
        const coins = await getAccountNetCoins(myAccount, currentNet?.id);
        setCoins(
          (coins as LuckyFormValues['coin'][]).filter(
            (coin) => coin.isMain || Number(coin.currencyPrice)
          )
        );
      })();
    }
  }, [myAccount, getAccountNetCoins, currentNet?.id]);

  useEffect(() => {
    if (gasFeeInfo.reserve_lamports_str && curCoin) {
      (async () => {
        const value = await getCurrencyPrice(
          curCoin,
          curFiat.symbol,
          gasFeeInfo.reserve_lamports_str
        );
        setGasFeeUSD(value);
      })();
    }
  }, [getCurrencyPrice, gasFeeInfo.reserve_lamports_str, curCoin, curFiat?.symbol]);

  useLayoutEffect(() => {
    if (!client?.Api) {
      return;
    }
    (async () => {
      const { data } = await client.Api.luckyBox
        .getLuckyboxSupportChain()
        .then((res) => {
          if (res.code && res.code !== 1) {
            throw res;
          }
          return res;
        })
        .catch(() => {
          return {
            data: [{ chain_type: 'Solana', list: ['1'] }],
          };
        });
      setChains(data);
    })();
  }, [client?.Api]);

  useEffect(() => {
    if (type === LuckyType.Exclusive) {
      return run();
    }
  }, [type, run]);

  return (
    <form onSubmit={onSubmit}>
      <Box className="LuckyFormContent scrollHover" px="5" pb="5" maxH="440px" overflowY="auto">
        <Stack gap="5" align="flex-start">
          {type !== LuckyType.Exclusive && (
            <Field.Root invalid={!!errors.quantity}>
              <Field.Label>{t('chat.quantityText')}</Field.Label>
              <InputGroup
                endElement={
                  isGroup && (
                    <Box fontSize="xs" color="fg.muted" fontWeight="medium">
                      {t('chat.groupMembersWithCountText', { count: groupInfo?.member_num })}
                    </Box>
                  )
                }
              >
                <Input
                  {...register('quantity', {
                    required: t('tips.pleaseEnterPositiveIntegerText'),
                    pattern: {
                      value: /^[0-9]*$/,
                      message: t('tips.pleaseEnterPositiveIntegerText'),
                    },
                    validate: (value) => {
                      if (
                        value &&
                        groupInfo?.member_num &&
                        0 < Number(value) &&
                        Number(value) <= groupInfo?.member_num
                      ) {
                        return true;
                      } else if (0 === Number(value)) {
                        return t('tips.quantityGreaterThanText', { count: 0 });
                      }
                      return t('tips.moreThanTheGroupSizeText');
                    },
                    onChange: () => trigger('quantity'),
                  })}
                  placeholder={t('chat.enterQuantityText')}
                  variant="subtle"
                  bg="white"
                  borderRadius="lg"
                  size="xl"
                  onBlur={(e) => setQuantity(e.target.value)}
                />
              </InputGroup>
              <Field.ErrorText>{errors.quantity?.message}</Field.ErrorText>
            </Field.Root>
          )}

          {type === LuckyType.Exclusive && (
            <Field.Root invalid={!!errors.sendTo}>
              <Field.Label>{t('chat.sendToText')}</Field.Label>
              <Controller
                control={control}
                name="sendTo"
                render={({ field }) => (
                  <Select.Root
                    collection={membersCollection}
                    cursor="pointer"
                    bg="white"
                    borderRadius="lg"
                    size="lg"
                    {...register(field.name)}
                    value={sendTo ? [sendTo] : undefined}
                    onValueChange={({ value }) => {
                      const val = value?.[0] as string;
                      setSendTo(val);
                      field.onChange(val);
                    }}
                  >
                    <Select.HiddenSelect />
                    <Select.Control>
                      <Select.Trigger cursor="pointer" borderRadius="lg">
                        <Select.ValueText placeholder={t('chat.selectSendToText')} />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content>
                        {membersCollection.items.map((member) => {
                          return member.user_id === user?.user_id ? null : (
                            <Select.Item item={member} key={member.value}>
                              <UserItem
                                {...member}
                                onAvatarClick={() => onMemberAvatarClick?.(member)}
                                cursor="pointer"
                              />
                              <Select.ItemIndicator />
                            </Select.Item>
                          );
                        })}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                )}
              />
            </Field.Root>
          )}

          <Field.Root invalid={!!errors.myAccount}>
            <Field.Label>{t('chat.myAccountsText')}</Field.Label>
            <Controller
              control={control}
              name="myAccount"
              render={({ field }) => (
                <Select.Root
                  collection={accountsCollection}
                  cursor="pointer"
                  bg="white"
                  borderRadius="lg"
                  size="lg"
                  {...register(field.name)}
                  value={myAccount ? [myAccount] : undefined}
                  onValueChange={({ value }) => {
                    const val = value?.[0] as DerivedWalletId;
                    setMyAccount(val);
                    field.onChange(val);
                  }}
                >
                  <Select.HiddenSelect />
                  <Select.Control>
                    <Select.Trigger cursor="pointer" borderRadius="lg">
                      <Select.ValueText placeholder={t('chat.selectMyAccountText')} />
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content gap="2">
                      {accountsCollection.items.map((account) => (
                        <Select.Item
                          item={account}
                          key={account.value}
                          p="0"
                          pr="2"
                          borderRadius="lg"
                          borderWidth="1px"
                          borderColor={myAccount === account.id ? 'brand.100' : 'grey.64'}
                          bg="white"
                        >
                          <VStack
                            minW="0"
                            flex="1"
                            p="2"
                            py="1"
                            borderRadius="lg"
                            cursor="pointer"
                            gap="0"
                            alignItems="start"
                          >
                            <Flex alignItems="center" gap="1" lineHeight="normal" w="full">
                              <Box fontSize="sm" fontWeight="semibold" truncate>
                                {account.name}
                              </Box>
                              {!account.isBackupMnemonic && (
                                <Box
                                  flexShrink="0"
                                  borderRadius="xs"
                                  fontSize="xs"
                                  px="1"
                                  bg="rgba(255, 0, 21, 0.10)"
                                  color="red.100"
                                >
                                  {t('tips.noBackup')}
                                </Box>
                              )}
                            </Flex>
                            {/* <Box fontSize="xs" color="fg.muted">
                              123
                            </Box> */}
                            <Flex
                              alignItems="center"
                              gap="1"
                              fontSize="xs"
                              fontWeight="medium"
                              minW="0"
                              color="fg.muted"
                              w="full"
                              overflow="hidden"
                            >
                              <Box truncate>
                                {currentNet?.chainType &&
                                  account.seriesWallets[currentNet.chainType]?.walletAddress}
                              </Box>
                              <Box
                                cursor="pointer"
                                color="fg.muted"
                                onClick={(e) => {
                                  if (!currentNet?.chainType) return;
                                  e.stopPropagation();
                                  tools.copyToClipboard(
                                    account.seriesWallets[currentNet.chainType]?.walletAddress
                                  );
                                }}
                              >
                                <CopyIcon />
                              </Box>
                            </Flex>
                          </VStack>
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
              )}
            />
          </Field.Root>

          <Field.Root invalid={!!errors.amount}>
            <Field.Label>
              {type === LuckyType.Identical ? t('chat.amountEachText') : t('chat.amountText')}
            </Field.Label>
            <Box w="full" bg="white" borderRadius="lg" py="1">
              <InputGroup
                isolation="initial"
                paddingInlineEnd="28"
                endElement={
                  <Field.Root>
                    <Select.Root
                      collection={coinsCollection}
                      cursor="pointer"
                      bg="white"
                      borderRadius="lg"
                      size="lg"
                      variant="subtle"
                      {...register('coin', { required: true })}
                      value={coin ? [coin] : undefined}
                      onValueChange={({ value }) => {
                        const val = value?.[0];
                        setCoin(val);
                      }}
                    >
                      <Select.HiddenSelect />
                      <Select.Control>
                        <Select.Trigger cursor="pointer" borderRadius="lg" p="0">
                          {curCoin ? (
                            <Flex p="1.5" mr="4.5" alignItems="center" gap="1" cursor="pointer">
                              <Box pos="relative">
                                <Image
                                  src={curCoin?.logo || (DefAvatarIcon as any)}
                                  alt="info"
                                  width="7"
                                  height="7"
                                  borderRadius="full"
                                  onError={onImageError}
                                />
                                <Float placement="bottom-end" offsetX="1" offsetY="1">
                                  <Image
                                    src={curCoin?.chainLogo || (DefAvatarIcon as any)}
                                    alt="info"
                                    width="3"
                                    height="3"
                                    borderRadius="full"
                                  />
                                </Float>
                              </Box>
                              <Box fontSize="sm" fontWeight="medium" color="fg">
                                {curCoin?.symbol}
                              </Box>
                            </Flex>
                          ) : (
                            <Select.ValueText placeholder={t('chat.selectCoinText')} />
                          )}
                        </Select.Trigger>
                        <Select.IndicatorGroup p={1.5}>
                          <Select.Indicator />
                        </Select.IndicatorGroup>
                      </Select.Control>
                      <Select.Positioner>
                        <Select.Content>
                          {coinsCollection.items?.map((option) => (
                            <Select.Item item={option} key={option.value} cursor="pointer" p="1">
                              <Flex
                                borderRadius="full"
                                alignItems="center"
                                gap="1"
                                cursor="pointer"
                              >
                                <Box pos="relative" w="5">
                                  <Image
                                    src={option?.logo}
                                    alt="info"
                                    width="5"
                                    height="5"
                                    borderRadius="full"
                                  />
                                  <Float placement="bottom-end" offsetX="1" offsetY="1">
                                    <Image
                                      src={option?.chainLogo}
                                      alt="info"
                                      width="3"
                                      height="3"
                                      borderRadius="full"
                                    />
                                  </Float>
                                </Box>

                                <Box fontSize="sm" fontWeight="medium" color="fg">
                                  {option?.symbol || option?.name}
                                </Box>
                              </Flex>
                              <Select.ItemIndicator />
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Positioner>
                    </Select.Root>
                  </Field.Root>
                }
              >
                <Input
                  {...register('amount', {
                    required: t('tips.pleaseEnterAmountText'),
                    pattern: {
                      value: new RegExp(`^([1-9]\\d*|0)(\\.\\d{1,${curCoin?.decimal || 9}})?$`),
                      message: t('tips.pleaseEnterValidNumericalAmountText'),
                    },
                    validate: (value) => {
                      if (value && Number(value) < 0.01) {
                        return t('tips.minimumAmountNotLessThanText', { amount: 0.01 });
                      }
                    },
                    onChange: () => trigger('amount'),
                  })}
                  marginInlineStart="1"
                  paddingInlineEnd="0"
                  paddingRight="4.5"
                  placeholder={t('chat.amountText')}
                  variant="subtle"
                  bg="white"
                  borderRadius="lg"
                  size="xl"
                  fontWeight="semibold"
                  onBlur={(e) => setAmount(e.target.value)}
                />
              </InputGroup>
              <Box mt="1" mx="3" borderBottom="1px solid" borderColor="bg.subtle" />
              <InputGroup
                pr="44px"
                endElement={
                  <Flex hidden={!message} fontSize="xs" fontWeight="medium" color="fg">
                    <Box>{message?.length}</Box>/<Box color="fg.muted">50</Box>
                  </Flex>
                }
              >
                <Textarea
                  {...register('message')}
                  resize="none"
                  rows={1}
                  placeholder={t('chat.messageForTransferText')}
                  variant="subtle"
                  maxLength={50}
                  defaultValue={t('chat.bestWishesText')}
                  bg="white"
                  borderRadius="lg"
                  size="xl"
                  border="none"
                  borderColor="transparent"
                />
              </InputGroup>
            </Box>
            <Field.ErrorText>{errors.amount?.message}</Field.ErrorText>
            <Box w="full" fontSize="xs" color="fg.muted" fontWeight="medium" textAlign="right">
              {t('chat.balanceText')}:
              <Box px="1" as="span">
                <FormatNumber
                  value={Number(curCoin?.balance || 0)}
                  minimumFractionDigits={Number(curCoin?.decimal || 5)}
                  minimumSignificantDigits={3}
                />
              </Box>
              {curCoin?.symbol}
            </Box>
          </Field.Root>

          <Field.Root invalid={!!errors.coverBgType}>
            <Field.Label>{t('chat.coverText')}</Field.Label>
            <Controller
              control={control}
              name="coverBgType"
              render={({ field }) => (
                <Select.Root
                  collection={coverOptions}
                  cursor="pointer"
                  bg="white"
                  borderRadius="lg"
                  size="lg"
                  {...register(field.name)}
                  value={coverBgType ? [coverBgType] : undefined}
                  onValueChange={({ value }) => {
                    const val = value?.[0] as CoverBgType;
                    setCoverBgType(val);
                    field.onChange(val);
                  }}
                >
                  <Select.HiddenSelect />
                  <Select.Control>
                    <Select.Trigger cursor="pointer" borderRadius="lg">
                      <Select.ValueText placeholder={t('chat.selectCoverText')}>
                        {t(
                          coverOptions.items.find((option) => option.value === coverBgType)
                            ?.label || ''
                        )}
                      </Select.ValueText>
                      <Box mr="5" fontSize="6xl">
                        {coverOptions.items.find((option) => option.value === coverBgType)?.icon}
                      </Box>
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      {coverOptions.items.map((option) => (
                        <Select.Item item={option} key={option.value} cursor="pointer">
                          <Flex alignItems="center" gap="2">
                            <Box fontSize="9xl">{option.icon}</Box>
                            <Box>
                              <Box>{t(option.label)}</Box>
                              <Box fontSize="xs" color="fg.muted" fontWeight="medium">
                                {t(option.description)}
                              </Box>
                            </Box>
                          </Flex>
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
              )}
            />
          </Field.Root>
        </Stack>
      </Box>
      <Box className="LuckyFormFooter" px="5">
        <Box mb="5" w="full" borderBottom="1px solid" borderColor="blackAlpha.10" />

        {isAmountGtBalance && (
          <Flex alignItems="center" justifyContent="center" gap="1" color="red" my="5">
            <WarningIcon />
            {t('tips.balanceNotSufficientText', { symbol: curCoin?.symbol })}
          </Flex>
        )}

        {!isAmountGtBalance && isMinAmount && (
          <Flex alignItems="center" justifyContent="center" gap="1" color="red" my="5">
            <WarningIcon /> {t('tips.eachLuckyBoxMustBeAtLeastText', { amount: 0.01 })}
          </Flex>
        )}
        <Box w="full">
          <Flex pb="2" justifyContent="space-between">
            <Box fontSize="sm" fontWeight="medium">
              {t('chat.totalText')}
            </Box>
            <Box fontSize="xl" fontWeight="semibold">
              {gasFeeInfo.expected_amount_str} {curCoin?.symbol}
            </Box>
          </Flex>
          <Box fontSize="xs" color="fg.muted" fontWeight="medium" textAlign="right">
            {t('chat.gasFeeText')}:{gasFeeInfo.reserve_lamports_str} {curCoin?.symbol} ≈{' '}
            {curFiat.sign}
            {gasFeeUSD}
          </Box>
        </Box>

        <Flex justifyContent="flex-end" pt="5">
          <Button
            disabled={!isValid || isAmountGtBalance || isMinAmount}
            type="submit"
            minW="120px"
            borderRadius="xl"
            loading={loading}
            _disabled={{ bg: 'grey.80', color: 'fg.subtle' }}
          >
            {t('buttons.confirm')}
          </Button>
        </Flex>
      </Box>
    </form>
  );
}
